import axiosClient from '../axiosClient';
import type { AxiosRequestConfig } from 'axios';
import type {
  DataViewResponse,
  CreateDataViewRequest,
  CopyDataViewRequest,
  UpdateDataViewRequest,
  PreviewQueryRequest,
  PreviewQueryResponse,
  RunDataViewRequest,
  DataViewRunResponse,
  ExportDataViewRequest,
  DataViewExportOptionsResponse,
  DataViewColumnDisplayResponse,
  DataViewAvailableColumnsResponse,
  DataViewRelationLookupResponse,
  UpdateDataViewColumnDisplayPreferencesRequest
} from '@/api/contracts/dataModel';
import type { DistinctValuesRequest, DistinctValuesResponse, QueryOperatorOption } from '@/api/contracts/queryResult';
import { assertGuidString } from '@/utils/guid';

/**
 * Henter backend-eid operator-katalog for datavisning-filter. Frontend hardkoder
 * ikke lenger SQL-operator-vokabularet — backend er autoritativ kilde.
 */
export async function fetchDataViewOperators (signal?: AbortSignal): Promise<QueryOperatorOption[]> {
  const response = await axiosClient.get<QueryOperatorOption[]>('data-views/operators', { signal });
  return response.data;
}

export async function getDataViews (
  options?: { modelId?: string; menuItemKey?: string },
  signal?: AbortSignal
): Promise<DataViewResponse[]> {
  const params: { modelId?: string; menuItemKey?: string } = {};
  if (options?.menuItemKey) params.menuItemKey = options.menuItemKey;
  else if (options?.modelId) params.modelId = assertGuidString(options.modelId, 'modelId');
  const config: AxiosRequestConfig = {
    signal,
    params: Object.keys(params).length > 0 ? params : undefined
  };
  const response = await axiosClient.get<DataViewResponse[]>('data-views', config);
  return response.data;
}

export async function getDataViewById (
  id: string,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataViewResponse>(`data-views/${assertGuidString(id, 'viewId')}`, config);
  return response.data;
}

export async function resolveDataViewIdByPublicId (
  publicId: string,
  signal?: AbortSignal
): Promise<string> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<string>(`data-views/resolve/${publicId}`, config);
  return response.data;
}

export async function createDataView (
  request: CreateDataViewRequest,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataViewResponse>('data-views', request, config);
  return response.data;
}

export async function copyDataView (
  id: string,
  request: CopyDataViewRequest,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataViewResponse>(
    `data-views/${assertGuidString(id, 'viewId')}/copy`,
    request,
    config
  );
  return response.data;
}

export async function updateDataView (
  id: string,
  request: UpdateDataViewRequest,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.put<DataViewResponse>(`data-views/${assertGuidString(id, 'viewId')}`, request, config);
  return response.data;
}

export async function updateDataViewColumnDisplayPreferences (
  id: string,
  request: UpdateDataViewColumnDisplayPreferencesRequest,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.put<DataViewResponse>(
    `data-views/${assertGuidString(id, 'viewId')}/column-display-preferences`,
    request,
    config
  );
  return response.data;
}

export async function updateMyDataViewColumnDisplayPreferences (
  id: string,
  request: UpdateDataViewColumnDisplayPreferencesRequest,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.put(
    `data-views/${assertGuidString(id, 'viewId')}/my-column-display-preferences`,
    request,
    config
  );
}

export async function deleteDataView (
  viewId: string,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.delete(`data-views/${assertGuidString(viewId, 'viewId')}`, config);
}

export async function previewDataView (
  id: string,
  request: PreviewQueryRequest,
  signal?: AbortSignal
): Promise<PreviewQueryResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<PreviewQueryResponse>(
    `data-views/${assertGuidString(id, 'viewId')}/preview`,
    request,
    config
  );
  return response.data;
}

export async function runDataView (
  id: string,
  request: RunDataViewRequest,
  signal?: AbortSignal
): Promise<DataViewRunResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedId = String(id || '').trim();
  const response = await axiosClient.post<DataViewRunResponse>(
    `data-views/${encodeURIComponent(normalizedId)}/run`,
    request,
    config
  );
  return response.data;
}

export async function getColumnDisplayMetadata (
  viewId: string,
  signal?: AbortSignal
): Promise<DataViewColumnDisplayResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataViewColumnDisplayResponse>(
    `data-views/${assertGuidString(viewId, 'viewId')}/column-display-metadata`,
    config
  );
  return response.data;
}

/**
 * US 2836: henter den backend-autoritative lista over valgbare kolonner (rå + aggregat-utdata) for
 * en datavisning. Editorens kolonne-velger rendrer denne i stedet for å utlede valgbarhet/aggregat
 * fra modellen selv.
 */
export async function getDataViewAvailableColumns (
  viewId: string,
  signal?: AbortSignal
): Promise<DataViewAvailableColumnsResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataViewAvailableColumnsResponse>(
    `data-views/${assertGuidString(viewId, 'viewId')}/available-columns`,
    config
  );
  return response.data;
}

export async function runDataViewRow (
  id: string,
  rowId: number,
  signal?: AbortSignal
): Promise<DataViewRunResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedId = String(id || '').trim();
  const response = await axiosClient.get<DataViewRunResponse>(
    `data-views/${encodeURIComponent(normalizedId)}/run/row`,
    { ...config, params: { rowId } }
  );
  return response.data;
}

export interface CreateStandardDataViewRequest {
  modelId: string;
  name: string;
  type: 'RawData' | 'Active' | 'Inactive';
  description?: string | null;
  overwriteViewId?: string | null;
}

export async function createStandardDataView (
  request: CreateStandardDataViewRequest,
  signal?: AbortSignal
): Promise<DataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.post<DataViewResponse>('data-views/standard', request, config);
  return response.data;
}

export async function fetchDataViewDistinctValues (
  id: string,
  request: DistinctValuesRequest,
  signal?: AbortSignal
): Promise<DistinctValuesResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedId = String(id || '').trim();
  const response = await axiosClient.post<DistinctValuesResponse>(
    `data-views/${encodeURIComponent(normalizedId)}/distinct-values`,
    request,
    config
  );
  return response.data;
}

export interface ExportDataViewResponse {
  taskId: string;
}

export async function getDataViewExportOptions (
  id: string,
  signal?: AbortSignal
): Promise<DataViewExportOptionsResponse> {
  const normalizedId = String(id || '').trim();
  if (!normalizedId) {
    throw new Error('viewId mangler for export-options.');
  }
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<DataViewExportOptionsResponse>(
    `data-views/${normalizedId}/export-options`,
    config
  );
  return response.data;
}

export async function exportDataView (
  id: string,
  request: ExportDataViewRequest,
  signal?: AbortSignal
): Promise<ExportDataViewResponse> {
  const config: AxiosRequestConfig = { signal };
  const normalizedId = String(id || '').trim();
  if (!normalizedId) {
    throw new Error('targetId mangler for eksport.');
  }

  const payload = {
    targetId: normalizedId,
    ...request
  };

  const response = await axiosClient.post<ExportDataViewResponse>(
    'exports',
    payload,
    config
  );
  return response.data;
}

export async function fetchDataViewRelationLookup (
  viewId: string,
  columnId: string,
  q: string = '',
  take: number = 20,
  signal?: AbortSignal
): Promise<DataViewRelationLookupResponse> {
  const config: AxiosRequestConfig = {
    signal,
    params: {
      columnId,
      q,
      take
    }
  };
  const response = await axiosClient.get<DataViewRelationLookupResponse>(
    `data-views/${assertGuidString(viewId, 'viewId')}/relation-lookup`,
    config
  );
  return response.data;
}

// WI 2184: slår opp én koblet rad på dens ICSRowId_ (samme {value,label} som dropdown-søket).
// Brukes til å auto-velge en nyopprettet koblet verdi. Returnerer 0/1 item.
export async function fetchDataViewRelationLookupByRowId (
  viewId: string,
  columnId: string,
  rowId: number,
  signal?: AbortSignal
): Promise<DataViewRelationLookupResponse> {
  const config: AxiosRequestConfig = {
    signal,
    params: {
      columnId,
      rowId
    }
  };
  const response = await axiosClient.get<DataViewRelationLookupResponse>(
    `data-views/${assertGuidString(viewId, 'viewId')}/relation-lookup`,
    config
  );
  return response.data;
}

// WI #1357: audit + fix av systemkolonne-defaults i datavisninger.
export interface SystemColumnAuditEntry {
  viewId: string;
  name: string;
  modelId: string;
  problematicColumnCount: number;
}

export interface SystemColumnAuditResponse {
  entries: SystemColumnAuditEntry[];
}

export interface FixSystemColumnDefaultsResponse {
  fixedCount: number;
  fixedViewIds: string[];
}

export async function auditSystemColumnDefaults (signal?: AbortSignal): Promise<SystemColumnAuditResponse> {
  const response = await axiosClient.get<SystemColumnAuditResponse>('data-views/system-columns/audit', { signal });
  return response.data;
}

export async function fixSystemColumnDefaults (
  viewIds: string[],
  signal?: AbortSignal
): Promise<FixSystemColumnDefaultsResponse> {
  const response = await axiosClient.post<FixSystemColumnDefaultsResponse>(
    'data-views/system-columns/fix',
    { viewIds },
    { signal }
  );
  return response.data;
}
